<template>
    <div class="dochub-resource-display" :class="wrapperClasses">
        <div :class="resourceClasses">
            <div class="d-flex align-items-baseline gap-3">
                <h5 class="mb-0">{{ resource.name }}</h5>
            </div>
            <p v-if="resource.description" class="text-muted mt-1 mb-0" :class="edit ? 'font-size-70' : 'font-size-80'">{{ resource.description }}</p>
        </div>
        <div :class="versionClasses">
            <div 
                :class="versionTextAreaClasses"
            >
                <div 
                    v-if="resource.active_version && resource.active_version.id" 
                    class="d-flex justify-content-start gap-3 align-items-baseline" 
                    :class="edit ? 'font-size-70' : 'font-size-70'"
                >
                    <span class="text-gray-700 font-weight-500 text-uppercase">Version {{ resource.active_version.version_number }}</span>
                    <span class="text-muted font-weight-500">{{ formatDate(resource.active_version.created) }}</span>
                </div>
                <div 
                    v-if="resource.active_version && resource.active_version.version_notes" 
                    class="text-show-breaks text-muted"
                    :class="edit ? 'font-size-70' : 'font-size-70'"
                >
                    {{ resource.active_version.version_notes }}
                </div>
            </div>
            <div :class="buttonAreaClasses">
                <div v-if="resource.active_version && resource.active_version.cloudflare_video_url" class="w-100">
                    <DocHubCloudflareVideoButton
                        :resource="resource"
                        :buttonProps="{variant: 'subtle-primary', size: finalButtonSize}"
                    ></DocHubCloudflareVideoButton>
                </div>
                <div v-else-if="resource.active_version && Array.isArray(resource.active_version.files_list) && resource.active_version.files_list.length" class="w-100">
                    <base-s-3-file-download-button
                        v-if="resource.active_version.files_list.length === 1"
                        :fileData="resource.active_version.files_list[0]"
                        v-bind="finalFileDownloadButtonProps"
                    ></base-s-3-file-download-button>                                                                
                    <b-button 
                        v-else
                        v-b-toggle="`resource-version-files-collapse-${resource.active_version.id}`"
                        variant="subtle-primary"
                        :size="finalButtonSize"
                        block
                        class="resource-version-multiplefiles-button d-flex align-items-center justify-content-between no-outline"
                    >  <!-- only showing collapse for MULTIPLE files -->
                        <div class="d-flex align-items-center justify-content-start">
                            <span class="font-weight-400">{{ resource.active_version.files_list.length }} Files</span>
                        </div>
                        <div class="d-flex align-items-center justify-content-end">
                            <span class="mr-2 text-uppercase font-size-70 font-weight-400">
                                <span class="collapsed-text">View Files</span>
                                <span class="not-collapsed-text">Hide</span>
                            </span>
                            <b-icon icon="chevron-down" class="collapse-icon"></b-icon>
                        </div>
                    </b-button>
                    <b-collapse 
                        :id="`resource-version-files-collapse-${resource.active_version.id}`" 
                    >
                        <base-s-3-file-download-button-group
                            :fileData="resource.active_version.files_list"
                            bundleDownloadLocation="below"
                            vertical
                            class="w-100"
                            :buttonProps="finalFileDownloadButtonProps"
                        ></base-s-3-file-download-button-group>
                    </b-collapse>
                </div>
                <b-button 
                    v-else-if="edit" 
                    key="requestCreateVersionButton" 
                    :size="finalButtonSize" 
                    variant="sharp-warning" 
                    block 
                    @click="requestToCreateVersion"
                >
                    <b-icon icon="plus-circle" class="mr-2"></b-icon>
                    Add Files (REQUIRED)
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import { FormatDateHelper } from '@/js/filters-helpers';
import DocHubCloudflareVideoButton from './DocHubCloudflareVideoButton.vue';


const CLASSES_FOR_LAYOUTS = {
    // 'EDITOR OLD': {  // used when edit = true
    //     wrapper: ['d-flex', 'gap-4', 'align-items-center', 'w-100'],
    //     resource: ['w-50'],
    //     version: ['w-50', 'd-flex', 'flex-column', 'gap-1'],
    //     versionTextArea: [],
    //     buttonArea: [],
    //     fileDownloadButtonProps: {
    //         buttonVariant: 'subtle-primary',
    //         block: true,
    //     }
    // },
    // 'EDITOR': {  // in new editor, not in company settings, things are wider, so we can just use the LIST layout
    //     wrapper: ['d-flex', 'gap-5', 'align-items-start', 'w-100'],
    //     resource: ['w-40'],
    //     version: ['align-self-center', 'w-60', 'd-flex', 'flex-row', 'gap-4'],
    //     versionTextArea: ['w-50', 'pt-1'],  // just added a smidge of top padding to make it look slightly more vertically centered
    //     buttonArea: ['w-50', 'align-self-center'],
    //     fileDownloadButtonProps: {
    //         buttonVariant: 'subtle-primary',
    //         block: true,
    //     }
    // },
    // 'SMALL_CARDS': {
    //     wrapper: ['d-flex', 'flex-column', 'justify-content-between', 'gap-4', 'h-100', 'w-100'],  // justify-content-between to push version to bottom
    //     resource: ['w-100'],
    //     version: ['w-100', 'd-flex', 'flex-column', 'gap-1'],
    //     versionTextArea: [],
    //     buttonArea: ['mt-2'],
    //     fileDownloadButtonProps: {
    //         buttonVariant: 'subtle-primary',
    //         block: true,
    //     }
    // },
    // 'LARGE_CARDS': {  // same as small cards, but with larger cards
    //     wrapper: ['d-flex', 'flex-column', 'justify-content-between', 'gap-4', 'h-100', 'w-100'],
    //     resource: ['w-100'],
    //     version: ['w-100', 'd-flex', 'flex-column', 'gap-1'],
    //     versionTextArea: [],
    //     buttonArea: ['mt-2'],
    //     fileDownloadButtonProps: {
    //         buttonVariant: 'subtle-primary',
    //         block: true,
    //     }
    // },
    // 'FULL_CARDS': {  // horizontal layout of the content
    //     wrapper: ['d-flex', 'gap-5', 'align-items-start', 'w-100'],
    //     resource: ['w-40'],
    //     version: ['align-self-center', 'w-60', 'd-flex', 'flex-row', 'gap-4'],
    //     versionTextArea: ['w-50', 'pt-1'],  // just added a smidge of top padding to make it look slightly more vertically centered
    //     buttonArea: ['w-50', 'align-self-center'],
    //     fileDownloadButtonProps: {
    //         buttonVariant: 'subtle-primary',
    //         block: true,
    //     }
    // },
    'GRID': {
        wrapper: ['d-flex', 'flex-column', 'justify-content-between', 'gap-4', 'h-100', 'w-100'],  // justify-content-between to push version to bottom
        resource: ['w-100'],
        version: ['w-100', 'd-flex', 'flex-column', 'gap-1'],
        versionTextArea: [],
        buttonArea: ['mt-2'],
        fileDownloadButtonProps: {
            buttonVariant: 'subtle-primary',
            block: true,
        }
    },
    'LIST': {  // also used when this.edit = true (from manager component)
        wrapper: ['d-flex', 'gap-5', 'align-items-center', 'w-100'],
        resource: ['w-30'],
        version: ['w-70', 'd-flex', 'flex-row', 'align-items-center', 'gap-4'],
        versionTextArea: ['w-40', 'pt-1'],  // just added a smidge of top padding to make it look slightly more vertically centered
        buttonArea: ['w-60', 'align-self-center'],
        fileDownloadButtonProps: {
            buttonVariant: 'subtle-primary',
            block: true,
        }
    },
}

export default {
    name: 'DocHubResourceDisplay',
    components: {
        DocHubCloudflareVideoButton,
    },
    mixins: [],
    props: {
        resource: {
            type: Object,
            required: true,
        },
        buttonSize: {  // changes things on fieldDownloadButtonProps, and other buttons so they are consistent
            type: String,
            required: false,  // only if you want to override the default
        },
        fileDownloadButtonProps: {
            type: Object,
            default: () => {
                return {};
            },
        },
        edit: {  // set true when in edit mode
            type: Boolean,
            default: false,
        },
        layoutType: {  // pass in from parent, must be one of the keys in CLASSES_FOR_LAYOUTS (from layout_type in backend enum)
            type: String,
            default: 'LIST',
        },
    },
    data() {
        return {

        }
    },
    computed: {
        // change these based on layout
        layoutClasses() {
            const layoutKey = this.edit ? 'LIST' : this.layoutType;
            return CLASSES_FOR_LAYOUTS[layoutKey] || {};
        },
        wrapperClasses() {
            return this.layoutClasses.wrapper || [];
        },
        resourceClasses() {
            return this.layoutClasses.resource || [];
        },
        versionClasses() {
            return this.layoutClasses.version || [];
        },
        versionTextAreaClasses() {
            return this.layoutClasses.versionTextArea || [];
        },
        buttonAreaClasses() {
            return this.layoutClasses.buttonArea || [];
        },
        finalButtonSize() {
            return this.buttonSize || 'sm';
        },
        finalFileDownloadButtonProps() {
            const fromProps = this.fileDownloadButtonProps || {};
            const defaults = this.layoutClasses.fileDownloadButtonProps ? {...this.layoutClasses.fileDownloadButtonProps} : {};
            // override default button size if we have prop set - change it if editing?
            if (this.finalButtonSize) {
                defaults.hideFilesize = this.buttonSize === 'sm' ? true : false;
                defaults.size = this.buttonSize; 
            }
            return {
                ...defaults,
                ...fromProps,
            };
        },
    },
    created() {
        
    },
    mounted() {
        
    },
    methods: {
        formatDate(dateString) {
            return FormatDateHelper(dateString);
        },
        requestToCreateVersion() {
            // emit event to parent to request creating a new version
            if (!this.edit) {
                return;
            }
            this.$emit('request-create-version', this.resource.id);
        },
    },
}
</script>

<style lang="scss" scoped>

.resource-version-multiplefiles-button {
    .collapse-icon {
        transition: transform 0.3s;
    }
    &.collapsed {
        .not-collapsed-text {
            display: none;
        }
        .collapse-icon {
            transform: rotate(0deg);
        }
    }
    &.not-collapsed {
        background-color: var(--gray-300);
        color: var(--gray-900);
        &:hover {
            background-color: var(--primary-100);
            color: var(--gray-900);
        }
        .collapsed-text {
            display: none;
        }
        .collapse-icon {
            transform: rotate(180deg);
        }
    }
}

</style>